import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Pane from 'components/Common/Pane'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { generateTransferCheckoutItem } from 'lib/checkout/experiences/cart'
import { addTransferToCheckout } from 'checkout/lib/analytics/checkoutUAMappers'
import CurrencyContext from 'contexts/currencyContext'
import { addGTMEvent } from 'api/googleTagManager'
import { fetchTransferOptions } from 'actions/ExperienceActions'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ExperienceTransferMultipleBooking from '../ExperienceTransfers/ExperienceTransferMultipleBooking'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { createSelector } from 'reselect'
import getTransferItems from 'checkout/selectors/view/items/getTransferItems'
import { addToCart, addToCartMultiItem } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { getAgentHubExperienceType } from 'lib/agentHub/agentHubUtils'
import Group from 'components/utils/Group'
import { addToUserCart } from 'actions/UserCartActions'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import ExperienceBookingModalPrices from '../ExperienceBookingModalPrices'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import { generateLoyaltyPointsCalculatorTransfersOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'

const TransferContainer = styled(Pane)`
  padding: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.S};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  position: relative;
`

const BottomContent = styled.div`
  display: grid;
  grid-template-areas:
    "save . ."
    "price . buy";
  grid-template-columns: auto 1fr auto;
  grid-row-gap: 4px;
  grid-column-gap: 20px;
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-template-areas: "price save buy";
  }
`

const PriceGridItem = styled.div`
  grid-area: price;
`
const SaveGridItem = styled.div`
  grid-area: save;

  ${mediaQueryUp.tablet} {
    text-align: right;
  }
`
const BuyGridItem = styled.div`
  grid-area: buy;
`

const getDefaultTransfers = createSelector(
  (state: App.State) => getTransferItems(state),
  (state: App.State, experienceId: string) => experienceId,
  (checkoutTransfers, experienceId): {
    toAirport?: App.ExperienceTransferView
    toHotel?: App.ExperienceTransferView
  } => {
    const existingCheckoutToHotel = checkoutTransfers.find(t => t.experienceId === experienceId && t.transfer.type === 'AIRPORT-TO-HOTEL')?.transfer
    const existingCheckoutToAirport = checkoutTransfers.find(t => t.experienceId === experienceId && t.transfer.type === 'HOTEL-TO-AIRPORT')?.transfer

    return {
      toAirport: existingCheckoutToAirport,
      toHotel: existingCheckoutToHotel,
    }
  },
)

interface Props {
  experienceView: App.ExperienceBookingView
  isLuxPlusEnabled: boolean
}

function ExperienceOfferPageTransferSelection(props: Props) {
  const { experienceView, isLuxPlusEnabled } = props
  const experience = experienceView.originalExperience
  const intl = useIntl()
  const totalSaleUnitLabel = intl.formatMessage({
    id: 'sM0W919-bPkNW1I1UaQV',
    defaultMessage: 'total',
    description: 'Default sale unit label for transfer selection pricing on experience offer page',
  })

  const [submitAction, setSubmitAction] = useState<'buyNow' | 'addToUserCart'>('buyNow')
  const [price, setPrice] = useState<{ total: number, totalMember: number, totalGrossPrice: number, isFromPrice: boolean }>({ total: 0, totalMember: 0, totalGrossPrice: 0, isFromPrice: true })
  const currency = useContext(CurrencyContext)
  const dispatch = useAppDispatch()
  const transferOptions = useAppSelector(state => state.experience.experienceTransferOptions[experience.id]?.transfers)
  const defaultTransfers = useAppSelector(state => getDefaultTransfers(state, experience.id))
  useEffect(() => {
    dispatch(fetchTransferOptions(experience.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = useCallback((transfers: Array<App.ExperienceTransferView>) => {
    const items = transfers.map(transfer => generateTransferCheckoutItem(experience, transfer))
    transfers.forEach(transfer => addGTMEvent(addTransferToCheckout(experienceView, transfer, currency)))

    if (submitAction === 'addToUserCart') {
      dispatch(addToUserCart(items))
      Analytics.trackEvent(addToCartMultiItem(items, { isMultiCart: true }))
      return
    } else {
      Analytics.trackEvent(addToCart())
    }

    dispatch(addToCheckoutCart(items))
    dispatch(navigateToCheckout())
  }, [currency, dispatch, experience, experienceView, submitAction])

  const onPriceChange = useCallback((total: number, totalMember: number, totalGrossPrice: number, isFromPrice: boolean) => setPrice({ total, totalMember, totalGrossPrice, isFromPrice }), [])

  const productType = useMemo(() => getAgentHubExperienceType(experience), [experience])

  const handleBuyNowClick = useCallback(() => {
    setSubmitAction('buyNow')
  }, [])

  const handleAddToUserCartClick = useCallback(() => {
    setSubmitAction('addToUserCart')
  }, [])

  const pointsEarnCalculationRequest = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorTransfersOptions({
      offerId: experience.id,
      price: price.total,
      memberPrice: price.totalMember,
      skip: !transferOptions,
    }),
  ], [price, transferOptions, experience.id])

  const findingTransfersLabel = intl.formatMessage({
    id: 'Cx0DaA9tuD4afKiysPPh',
    defaultMessage: 'Finding available transfers...',
    description: 'Loading message while fetching available transfer options for an experience',
  })
  const addToCartLabel = intl.formatMessage({
    id: 'VLlpwDjqsyjyV-RyWwgp',
    defaultMessage: 'Add to cart',
    description: 'Button label to add experience transfers to the shopping cart',
  })
  const buyNowLabel = intl.formatMessage({
    id: '_2c5KJpetIv4Vr7NxaMw',
    defaultMessage: 'Buy now',
    description: 'Button label to immediately purchase experience transfers',
  })

  return <VerticalSpacer gap={24}>
    <TransferContainer>
      {!transferOptions && <div>
        <LoadingIndicator inline visible>{findingTransfersLabel}</LoadingIndicator>
      </div>}
      {transferOptions && <ExperienceTransferMultipleBooking
        defaultToHotelTransfer={defaultTransfers.toHotel}
        defaultToAirportTransfer={defaultTransfers.toAirport}
        experience={experienceView}
        transferOptions={transferOptions}
        onSubmit={onSubmit}
        onPriceChange={onPriceChange}
        formId="offerPageTransferForm"
      />}
    </TransferContainer>
    <BottomContent>
      <PriceGridItem>
        <ExperienceBookingModalPrices
          totalPrice={price.total}
          totalMemberPrice={price.totalMember}
          totalGrossPrice={price.totalGrossPrice}
          isFromPrice={price.isFromPrice}
          ticketUnitLabel={price.isFromPrice && experienceView.ticketUnitLabel ? experienceView.ticketUnitLabel : totalSaleUnitLabel}
          discountPercentage={price.isFromPrice && experienceView.pricing.discountPercent >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW ? experienceView.pricing.discountPercent : undefined}
          discountMemberPercentage={price.isFromPrice && experienceView.pricing.memberDiscountPercentage >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW ? experienceView.pricing.memberDiscountPercentage : undefined}
          isLuxPlusOffer={isLuxPlusEnabled && experienceView.pricing.memberPrice > 0}
        />
        <PriceRowAgentHubCommission
          offerId={experience.id}
          productType={productType}
          offerLocations={[experience.location.name]}
        />
        <LoyaltyPointsEarnWithBreakdownModal
          calculationRequests={pointsEarnCalculationRequest}
          calculationType={price.isFromPrice ? 'estimate' : 'exact'}
          hideStatusCredits
        />
      </PriceGridItem>
      <TripGuard>
        <SaveGridItem>
          <ExperienceBookmarkButton offer={experience} />
        </SaveGridItem>
      </TripGuard>
      <BuyGridItem>
        <Group direction="vertical-reverse" gap={8} tabletDirection="horizontal">
          <TextButton
            kind="secondary"
            startIcon={<LineShoppingCartIcon />}
            size="large"
            type="submit"
            form="offerPageTransferForm"
            onClick={handleAddToUserCartClick}
          >
            {addToCartLabel}
          </TextButton>
          <TextButton
            kind="primary"
            size="large"
            type="submit"
            form="offerPageTransferForm"
            onClick={handleBuyNowClick}
          >
            {buyNowLabel}
          </TextButton>
        </Group>
      </BuyGridItem>
    </BottomContent>
  </VerticalSpacer>
}

const mapStateToProps = (state: App.State) => {
  return {
    isLuxPlusEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
  }
}

export default connect(mapStateToProps)(ExperienceOfferPageTransferSelection)
